import { useMemo, useState } from "react";
import { buildSummaryMatrix, type SummaryCell } from "../calc/governingCase";
import { API_521_SCENARIOS, findScenario } from "../data/scenarios";
import { toPsvCase, type CaseState } from "./caseState";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import {
  buildSummaryCsv,
  defaultSummaryCsvName,
  downloadCsv,
} from "./csvExport";

/** 표에 무엇을 채울지. */
type Metric = "flow" | "mass" | "area";

const ALL_ITEMS = API_521_SCENARIOS.map((s) => s.item);

/**
 * 인쇄 한 장에 넣을 밸브 열의 수.
 *
 * A4 가로(297 mm)에 좌우 여백 10 mm 씩을 빼면 인쇄 폭은 277 mm.
 *   - 시나리오 이름 열: "12. 내부 폭발 및 과도 압력 서지 (4.4.10)" 이 한 줄에
 *     들어가려면 약 68 mm 필요
 *   - 밸브 한 열: "216,560 kg/h" 가 잘리지 않으려면 약 29 mm 필요
 *   → (277 − 68) / 29 ≈ 7.2  →  **7 열**
 *
 * 이보다 밸브가 많으면 다음 장으로 넘기고, 그 장에도 시나리오 이름 열을
 * 다시 붙인다(엑셀의 '틀 고정'에 해당하는 효과).
 */
const COLUMNS_PER_PAGE = 7;

/** 배열을 n 개씩 끊는다. */
function chunk<T>(items: readonly T[], n: number): T[][] {
  if (items.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += n) out.push(items.slice(i, i + n));
  return out;
}

/**
 * 밸브(열) × API 521 시나리오 17종(행) 요약표.
 *
 * 행을 17개 **전부** 만드는 것이 요점이다. 검토하지 않은 항목이
 * 빈칸이 아니라 `N/A` 로 드러나야, 빠뜨린 것인지 해당 없는 것인지
 * 검토자가 구분할 수 있다.
 */
export function SummaryPanel({
  cases,
  unit,
}: {
  cases: readonly CaseState[];
  unit: PressureUnit;
}) {
  const [metric, setMetric] = useState<Metric>("flow");
  const [hideEmptyRows, setHideEmptyRows] = useState(false);

  const matrix = useMemo(
    () => buildSummaryMatrix(cases.map((c) => toPsvCase(c, unit)), ALL_ITEMS),
    [cases, unit],
  );

  const rows = useMemo(
    () =>
      hideEmptyRows
        ? matrix.rows.filter((r) => r.cells.some((c) => c.kind !== "na"))
        : matrix.rows,
    [matrix, hideEmptyRows],
  );

  /** 열을 인쇄 한 장 분량씩 끊는다. 각 묶음은 열 인덱스 목록이다. */
  const pages = useMemo(
    () => chunk(matrix.columns.map((_, i) => i), COLUMNS_PER_PAGE),
    [matrix.columns],
  );

  function cellText(c: SummaryCell): string {
    if (c.kind === "na") return "N/A";
    if (c.kind === "error") return "계산 불가";
    if (metric === "flow") {
      return c.flow === null
        ? "—"
        : `${c.flow.value.toLocaleString("ko-KR")} ${c.flow.unit}`;
    }
    if (metric === "mass") {
      return c.flow === null
        ? "—"
        : `${c.flow.massFlowKgH.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} kg/h`;
    }
    return c.requiredAreaMm2 === null
      ? "—"
      : `${c.requiredAreaMm2.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} mm²`;
  }

  function cellClass(c: SummaryCell): string {
    const parts = ["cell"];
    if (c.kind === "na") parts.push("cell--na");
    if (c.kind === "error") parts.push("cell--error");
    if (c.kind === "excluded") parts.push("cell--excluded");
    if (c.isGoverning) parts.push("cell--gov");
    return parts.join(" ");
  }

  if (cases.length === 0) {
    return (
      <section className="card">
        <h2>검토 요약</h2>
        <p className="hint">밸브를 먼저 추가하십시오.</p>
      </section>
    );
  }

  return (
    <>
      {/* 요약표는 열이 많아 가로 인쇄여야 한다.
          @page 는 문서 전역이라, 이 탭이 열려 있는 동안에만 이 style 이
          문서에 존재하게 해서 다른 탭(세로인 계산서)에 영향을 주지 않는다. */}
      <style>{`@page { size: A4 landscape; margin: 10mm; }`}</style>

      <section className="card no-print">
        <div className="row-head">
          <h2>요약표 출력</h2>
          <div className="savebar__actions">
            <button type="button" className="btn" onClick={() => window.print()}>
              인쇄 / PDF 저장
            </button>
            <button
              type="button"
              className="btn"
              onClick={() =>
                downloadCsv(
                  buildSummaryCsv(matrix, metric),
                  defaultSummaryCsvName(),
                )
              }
            >
              엑셀용 CSV 내보내기
            </button>
          </div>
        </div>
        <p className="hint">
          <b>인쇄 / PDF 저장</b>은 <b>A4 가로</b>로 나갑니다(자동 설정).
          밸브가 {COLUMNS_PER_PAGE}개를 넘으면 다음 장으로 넘기고, 그 장에도
          시나리오 이름 열을 다시 붙입니다.
          {pages.length > 1 && (
            <>
              {" "}
              현재 {matrix.columns.length}개 → <b>{pages.length}장</b>으로
              나뉩니다.
            </>
          )}
          <br />
          <b>CSV</b>는 현재 표시 중인 값(
          {metric === "flow"
            ? "소요 분출용량"
            : metric === "mass"
              ? "질량유량(PSM 기재용)"
              : "소요 면적"}
          )으로 내보냅니다.
        </p>
      </section>

      <section className="card sheet">
        <div className="row-head">
          <h2>검토 요약 — 밸브별 시나리오 비교</h2>
          <div className="row-head__controls no-print">
            <div className="seg">
              <button
                type="button"
                className={"seg__btn" + (metric === "flow" ? " seg__btn--on" : "")}
                onClick={() => setMetric("flow")}
              >
                소요 분출용량
              </button>
              <button
                type="button"
                className={"seg__btn" + (metric === "mass" ? " seg__btn--on" : "")}
                onClick={() => setMetric("mass")}
              >
                질량유량 (PSM)
              </button>
              <button
                type="button"
                className={"seg__btn" + (metric === "area" ? " seg__btn--on" : "")}
                onClick={() => setMetric("area")}
              >
                소요 면적
              </button>
            </div>
            <label className="scenario__check">
              <input
                type="checkbox"
                checked={hideEmptyRows}
                onChange={(e) => setHideEmptyRows(e.target.checked)}
              />
              해당 없는 행 숨기기
            </label>
          </div>
        </div>

        <p className="sheet__note">
          행은 API 521 Table 1 의 과압 시나리오 17종입니다. 어떤 밸브에 그
          시나리오가 없으면 <b>N/A</b> 로 표시됩니다 — 검토에서 빠뜨린 것인지
          해당 없는 것인지 구분하기 위해서입니다. 강조된 칸이 그 밸브의 지배
          케이스입니다. 표시값:{" "}
          <b>
            {metric === "flow"
              ? "소요 분출용량"
              : metric === "mass"
                ? "질량유량 (kg/h)"
                : "소요 면적 (mm²)"}
          </b>
          {metric === "mass" && (
            <>
              {" "}
              — 액체·과냉 액체 시나리오는 사이징 자체가 체적유량(L/min,
              API 520 Part I 액체식이 요구하는 단위)을 쓰지만, 이 값은 PSM
              명세서 기재용으로 비중·밀도를 곱해 kg/h 로 환산한 것입니다.
            </>
          )}
        </p>

        {/* 열 묶음마다 표를 하나씩 만든다.
            이렇게 해야 (1) 인쇄에서 열이 잘리지 않고 (2) 각 장에 시나리오
            이름 열이 다시 붙는다. 브라우저는 넓은 표를 열 기준으로
            페이지 분할해 주지 않는다. */}
        {pages.map((colIdx, pageNo) => (
          <div className="matrix-page" key={`page-${pageNo}`}>
            {pages.length > 1 && (
              <p className="matrix-page__label">
                {pageNo + 1} / {pages.length} —{" "}
                {colIdx
                  .map((i) => matrix.columns[i]!.psvCase.tag)
                  .join(", ")}
              </p>
            )}
            <div className="table-scroll">
              <table className="matrix">
                <thead>
                  <tr>
                    <th className="matrix__rowhead">
                      시나리오 (API 521 Table 1)
                    </th>
                    {colIdx.map((i) => (
                      <th
                        key={matrix.columns[i]!.psvCase.tag}
                        className="matrix__col"
                      >
                        {matrix.columns[i]!.psvCase.tag}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const def = findScenario(row.item);
                    return (
                      <tr key={row.item}>
                        <th className="matrix__rowhead" scope="row">
                          <span className="matrix__item">{row.item}</span>{" "}
                          {def?.conditionKo}
                          <span className="muted"> ({def?.section})</span>
                        </th>
                        {colIdx.map((i) => {
                          const c = row.cells[i]!;
                          return (
                            <td
                              key={matrix.columns[i]!.psvCase.tag}
                              className={cellClass(c)}
                              title={c.note ?? c.description ?? undefined}
                            >
                              {cellText(c)}
                              {c.count > 1 && (
                                <span className="muted">
                                  {" "}
                                  외 {c.count - 1}건
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th className="matrix__rowhead" scope="row">
                      지배 케이스
                    </th>
                    {colIdx.map((i) => {
                      const col = matrix.columns[i]!;
                      return (
                        <td
                          key={col.psvCase.tag}
                          className="cell cell--foot"
                        >
                          {col.result.governing
                            ? `${col.result.governing.entry.item}. ${col.result.governing.entry.label}`
                            : "—"}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <th className="matrix__rowhead" scope="row">
                      선정 오리피스
                    </th>
                    {colIdx.map((i) => {
                      const col = matrix.columns[i]!;
                      return (
                        <td key={col.psvCase.tag} className="cell cell--foot">
                          {col.result.selection?.ok
                            ? col.result.selection.orifice.designation +
                              (col.result.selection.valveCount > 1
                                ? ` × ${col.result.selection.valveCount}`
                                : "")
                            : "—"}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <th className="matrix__rowhead" scope="row">
                      설정압 (kPag)
                    </th>
                    {colIdx.map((i) => {
                      const col = matrix.columns[i]!;
                      return (
                        <td key={col.psvCase.tag} className="cell cell--foot">
                          {col.psvCase.setPressureKpaGauge.toLocaleString(
                            "ko-KR",
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ))}
      </section>

      <section className="card sheet">
        <h2>과압 상황 설명</h2>
        <p className="sheet__note">
          각 시나리오에 설계자가 적은 설명입니다. 계산서와 PSM 명세서에 그대로
          옮길 수 있습니다.
        </p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>밸브</th>
                <th>시나리오</th>
                <th>상황 설명</th>
              </tr>
            </thead>
            <tbody>
              {matrix.columns.flatMap((col) =>
                col.psvCase.scenarios.map((s) => (
                  <tr key={`${col.psvCase.tag}-${s.id}`}>
                    <td>{col.psvCase.tag}</td>
                    <td>
                      {s.item}. {s.label}
                    </td>
                    <td className="cell--desc">
                      {s.description || (
                        <span className="muted">— 미작성 —</span>
                      )}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
